import { useCallback, useEffect, useState } from 'react';
import { api } from '../api/client';
import { token as liveToken } from '../auth/session';
import { useAuth } from '../auth/AuthContext';
import { translate } from '../i18n/i18n';

export type Role = 'ADMIN' | 'TEACHER' | 'PARENT';

export type ParentAccess = { play: boolean; edit: boolean; talk: boolean };

export type School = {
  name: string;
  role: Role;
  staffId: string;
  aiEnabled: boolean;
  parentsTwoFactor: boolean;
  hasLogo: boolean;
  pupilLimit: number;
  pupils: number;
  access: ParentAccess | null;
};

export type Parent = {
  id: string;
  email: string;
  name: string | null;
  play: boolean;
  edit: boolean;
  talk: boolean;
  active: boolean;
  signedIn: boolean;
  lastSeenAt: number | null;
  learnerIds: string[];
};

export type Invited = { parent: Parent; emailed: boolean; link: string };

export type Staff = {
  id: string;
  email: string;
  name: string | null;
  role: Role;
  active: boolean;
  signedIn: boolean;
  lastSeenAt: number | null;
  you: boolean;
  classIds: string[];
};

export type SchoolClass = { id: string; name: string; teachers: { id: string; name: string }[]; pupils: number };

export type Pupil = { id: string; name: string; classIds: string[] };

export type PupilProgress = {
  id: string;
  name: string;
  classes: string[];
  answered: number;
  correctPct: number | null;
  daysPractised: number;
  lastPractisedAt: number | null;
};

export type ClassProgress = { id: string; name: string; pupils: number; active: number; answered: number; correctPct: number | null };

export type Overview = {
  days: number;
  pupils: number;
  activePupils: number;
  answered: number;
  correctPct: number | null;
  classes: ClassProgress[];
  pupilRows: PupilProgress[];
};

export type AuditEntry = { id: number; at: string; actor: string | null; action: string; subject: string | null; detail: string | null };

const listeners = new Set<() => void>();

export function refreshSchool() {
  listeners.forEach((l) => l());
}

export function useSchool(): { school: School | null; loaded: boolean } {
  const { token } = useAuth();
  const [school, setSchool] = useState<School | null>(null);
  const [loaded, setLoaded] = useState(false);
  const load = useCallback(() => {
    if (!token) { setSchool(null); setLoaded(true); return; }
    api<School>('/school', { token: true })
      .then((s) => { setSchool(s); setLoaded(true); })
      .catch(() => { setSchool(null); setLoaded(true); });
  }, [token]);
  useEffect(() => {
    load();
    listeners.add(load);
    return () => { listeners.delete(load); };
  }, [load]);
  return { school, loaded };
}

async function authorised(path: string, init: RequestInit = {}): Promise<Response> {
  const fresh = await liveToken();
  const res = await fetch(`/api${path}`, {
    ...init,
    headers: { ...(init.headers ?? {}), ...(fresh ? { Authorization: `Bearer ${fresh}` } : {}) },
    credentials: 'omit',
  });
  if (!res.ok) {
    let message = translate('HTTP {status}', { status: res.status });
    try {
      const body = await res.json();
      if (body && typeof body.message === 'string') message = translate(body.message);
    } catch { /* not json */ }
    throw new Error(message);
  }
  return res;
}

export async function download(path: string, filename: string) {
  const res = await authorised(path);
  const url = URL.createObjectURL(await res.blob());
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}

export async function logoUrl(): Promise<string | null> {
  try {
    const res = await authorised('/school/logo');
    return URL.createObjectURL(await res.blob());
  } catch {
    return null;
  }
}

export async function uploadLogo(file: File) {
  await authorised('/school/logo', { method: 'PUT', headers: { 'Content-Type': file.type }, body: file });
}

export function roleLabel(role: Role): string {
  return role === 'ADMIN' ? 'School admin' : role === 'PARENT' ? 'Parent' : 'Teacher';
}

export function useParentAccess(): ParentAccess {
  const { school } = useSchool();
  return school?.role === 'PARENT' && school.access ? school.access : { play: true, edit: true, talk: true };
}
